import { GroupByOption } from './types.esm.js';
import { Buffer } from 'buffer';
import { chunk, mapValues } from 'lodash';
import { stringifyAnyEntityRef } from '../utils/types.esm.js';
import { createApiRef } from '@backstage/core-plugin-api';
import { gzipSync } from 'zlib';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const cortexApiRef = createApiRef({
  id: "plugin.cortex.service"
});
function isString(val) {
  return typeof val === "string";
}
class CortexClient {
  constructor(options) {
    __publicField(this, "discoveryApi");
    __publicField(this, "identityApi");
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }
  async getScorecards() {
    return await this.get(`/api/backstage/v2/scorecards`);
  }
  async submitEntitySync(entities, gzipContents, teamOverrides, chunkSize) {
    var _a, _b;
    const syncChunkSize = chunkSize != null ? chunkSize : DEFAULT_CHUNK_SIZE;
    const post = async (path, body) => {
      return gzipContents ? await this.postVoidWithGzipBody(path, body) : await this.postVoid(path, body);
    };
    await this.postVoid("/api/backstage/v2/entities/sync-init");
    for (let customMappingsChunk of chunk(entities, syncChunkSize)) {
      await post(`/api/backstage/v2/entities/sync-chunked`, {
        entities: customMappingsChunk
      });
    }
    for (let teamOverridesTeamChunk of chunk(
      (_a = teamOverrides == null ? void 0 : teamOverrides.teams) != null ? _a : [],
      syncChunkSize
    )) {
      await post(`/api/backstage/v2/entities/sync-chunked`, {
        entities: [],
        teamOverrides: {
          teams: teamOverridesTeamChunk,
          relationships: []
        }
      });
    }
    for (let teamOverridesRelationshipsChunk of chunk(
      (_b = teamOverrides == null ? void 0 : teamOverrides.relationships) != null ? _b : [],
      syncChunkSize
    )) {
      await post(`/api/backstage/v2/entities/sync-chunked`, {
        entities: [],
        teamOverrides: {
          teams: [],
          relationships: teamOverridesRelationshipsChunk
        }
      });
    }
    return await this.post("/api/backstage/v2/entities/sync-submit");
  }
  async getServiceScores(entityRef) {
    return await this.get(`/api/backstage/v2/entities/scorecards`, {
      ref: stringifyAnyEntityRef(entityRef)
    });
  }
  async getServiceNextSteps(entityRef, scorecardId) {
    return await this.get(
      `/api/backstage/v2/entities/${scorecardId}/ladders/next-steps`,
      {
        ref: stringifyAnyEntityRef(entityRef)
      }
    );
  }
  async getHistoricalScores(scorecardId, entityRef, startDate, endDate) {
    const results = await this.get(
      `/api/backstage/v1/scorecards/${scorecardId}/scores/historical`,
      {
        ref: stringifyAnyEntityRef(entityRef),
        ...startDate && { startDate: startDate.toISOString() },
        ...endDate && { endDate: endDate.toISOString() }
      }
    );
    return results.sort((a, b) => {
      return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
    });
  }
  async getAverageHistoricalScores(scorecardId, groupBy, options) {
    const { ruleExpression, startDate, endDate } = options;
    const scores = await this.get(
      `/api/backstage/v1/scorecards/${scorecardId}/scores/historical-average`,
      {
        ...groupBy !== GroupByOption.ENTITY && {
          groupBy: groupBy.toUpperCase().replace(" ", "_")
        },
        ...ruleExpression && { ruleExpression },
        ...startDate && { startDate: startDate.toISOString() },
        ...startDate && { startDate: startDate.toISOString() },
        ...endDate && { endDate: endDate.toISOString() }
      }
    );
    return scores.map((score) => {
      return {
        identifier: score.identifier,
        scores: score.scores.sort((a, b) => {
          return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
        }),
        numberOfServices: 1
      };
    });
  }
  async getServiceScorecardScores(groupBy) {
    const args = groupBy !== GroupByOption.ENTITY ? {
      groupBy: groupBy.toUpperCase().replace(" ", "_")
    } : void 0;
    return await this.get(`/api/backstage/v1/scorecards/scores`, args);
  }
  async getScorecard(scorecardId) {
    return await this.get(`/api/backstage/v2/scorecards/${scorecardId}`);
  }
  async getScorecardLadders(scorecardId) {
    return await this.get(
      `/api/backstage/v1/scorecards/${scorecardId}/ladders`
    );
  }
  async getScorecardScores(scorecardId) {
    const response = await this.get(
      `/api/backstage/v2/scorecards/${scorecardId}/scores`
    );
    return response.scores;
  }
  async getScorecardRuleExemptions(scorecardId) {
    return await this.get(
      `/api/backstage/v1/scorecards/${scorecardId}/rules/exemptions`
    );
  }
  async getInitiatives() {
    return await this.get(`/api/backstage/v2/initiatives`);
  }
  async getInitiative(id) {
    return await this.get(`/api/backstage/v2/initiatives/${id}`);
  }
  async getInitiativeActionItems(id) {
    return await this.get(`/api/backstage/v1/initiatives/${id}/actionitems`);
  }
  async getInitiativeActionItemsForTeam(entityRef) {
    return await this.get(`/api/backstage/v1/teams/initiatives/action-items`, {
      ref: stringifyAnyEntityRef(entityRef)
    });
  }
  async getComponentActionItems(entityRef) {
    return await this.get(
      `/api/backstage/v1/entities/initiatives/actionitems`,
      {
        ref: stringifyAnyEntityRef(entityRef)
      }
    );
  }
  async getBulkComponentActionItems(entityRefs) {
    return await this.get(
      `/api/backstage/v1/entities/initiatives/actionitems`,
      {
        refs: entityRefs.map((entityRef) => stringifyAnyEntityRef(entityRef))
      }
    );
  }
  async getEntitySyncProgress() {
    return await this.get(`/api/backstage/v2/entities/progress`);
  }
  async getLastEntitySyncTime() {
    return await this.get(`/api/backstage/v2/entities/last-sync`);
  }
  async cancelEntitySync() {
    await this.delete(`/api/backstage/v2/entities/sync`);
  }
  async getUserOncallByEmail() {
    return this.get(`/api/backstage/v2/homepage/oncall`);
  }
  async getInsightsByEmail() {
    return this.get(`/api/backstage/v2/homepage/insights`);
  }
  async getCatalogEntities() {
    return this.get(`/api/backstage/v1/homepage/catalog`);
  }
  async getUserEntities() {
    return this.get(`/api/backstage/v2/entities/my-entities`);
  }
  async getUserPermissions() {
    return this.get(`/api/backstage/v2/permissions`);
  }
  async getSyncJobs() {
    return this.get(`/api/backstage/v2/jobs`);
  }
  async getExpiration() {
    return this.get(`/api/backstage/v1/entitlements/expiration-date`);
  }
  async getAllDomains() {
    return this.get(`/api/backstage/v1/domains`);
  }
  async getEntityDomainAncestors() {
    return this.get(`/api/backstage/v1/domains/hierarchies/ancestors`);
  }
  async getDomainHierarchies() {
    return await this.get(`/api/backstage/v1/domains/hierarchies/tree`);
  }
  async getTeamHierarchies() {
    return await this.get(`/api/backstage/v1/teams/hierarchies`);
  }
  async getAllTeams() {
    return this.get(`/api/backstage/v1/teams`);
  }
  async getAllTeamsByEntityId() {
    return this.get(`/api/backstage/v1/ownership/entities/teams`);
  }
  async getServiceGroups() {
    return await this.get("/api/backstage/v1/service-groups");
  }
  async getBasePath() {
    const proxyBasePath = await this.discoveryApi.getBaseUrl("proxy");
    return `${proxyBasePath}/cortex`;
  }
  async get(path, args) {
    const basePath = await this.getBasePath();
    const url = `${basePath}${path}`;
    const queryParams = args && Object.keys(args).flatMap((key) => {
      const val = args[key];
      if (isString(val)) {
        return [[key, val]];
      }
      return val.map((v) => [key, v]);
    }).map((kv) => {
      const [key, val] = kv;
      return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
    }).join("&");
    const queryUrl = queryParams ? `${url}?${queryParams}` : url;
    const response = await this.fetchAuthenticated(queryUrl);
    const body = await response.json();
    if (response.status === 404) {
      return void 0;
    } else if (response.status !== 200) {
      throw new Error(
        `Error communicating with Cortex: ${typeof body === "object" ? JSON.stringify(body) : body}`
      );
    }
    return body;
  }
  async post(path, body) {
    const basePath = await this.getBasePath();
    const url = `${basePath}${path}`;
    const response = await this.fetchAuthenticated(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" }
    });
    if (response.status !== 200) {
      throw new Error(`Error communicating with Cortex`);
    }
    return response.json();
  }
  async postVoid(path, body) {
    const basePath = await this.getBasePath();
    const url = `${basePath}${path}`;
    const response = await this.fetchAuthenticated(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" }
    });
    if (response.status !== 200) {
      throw new Error(`Error communicating with Cortex`);
    }
    return;
  }
  async postVoidWithGzipBody(path, body) {
    const basePath = await this.getBasePath();
    const url = `${basePath}${path}`;
    const input = Buffer.from(JSON.stringify(body), "utf-8");
    const compressed = gzipSync(input);
    const response = await this.fetchAuthenticated(url, {
      method: "POST",
      body: compressed,
      headers: {
        "Content-Type": "application/json",
        "Content-Encoding": "gzip"
      }
    });
    if (response.status !== 200) {
      throw new Error(`Error communicating with Cortex`);
    }
    return;
  }
  async delete(path, body) {
    const basePath = await this.getBasePath();
    const url = `${basePath}${path}`;
    const response = await this.fetchAuthenticated(url, {
      method: "DELETE",
      body: body && JSON.stringify(body),
      headers: body && { "Content-Type": "application/json" }
    });
    if (response.status !== 200) {
      throw new Error(`Error communicating with Cortex`);
    }
  }
  async fetchAuthenticated(input, init) {
    let token;
    let email;
    let displayName;
    if (this.identityApi !== void 0) {
      const [credentials, profileInfo] = await Promise.all([
        this.identityApi.getCredentials(),
        this.identityApi.getProfileInfo()
      ]);
      token = credentials.token;
      email = profileInfo.email;
      displayName = profileInfo.displayName;
    }
    const xCortexHeaders = mapValues(
      {
        "x-cortex-email": email != null ? email : "",
        "x-cortex-name": displayName != null ? displayName : ""
      },
      encodeURIComponent
    );
    const headers = {
      ...init == null ? void 0 : init.headers,
      Authorization: `Bearer ${(token != null ? token : "").replace(/^[Bb]earer\s+/, "").trim()}`,
      ...xCortexHeaders
    };
    if (token !== void 0) {
      return fetch(input, {
        ...init,
        headers
      });
    } else {
      return fetch(input, init);
    }
  }
}
const DEFAULT_CHUNK_SIZE = 1e3;

export { CortexClient, cortexApiRef };
//# sourceMappingURL=CortexClient.esm.js.map
